import { createContext, useContext } from "react";
import { useLoad } from "@/lib/api";

export const card = "rounded-3xl border border-[var(--lav-3)] bg-white p-5 sm:p-6";
export const inputCls = "w-full rounded-xl border border-input bg-white px-3 py-2 text-sm outline-none focus:border-[var(--violet)] disabled:bg-muted";

export const PageHead = ({ title, sub, children }) => (
  <div className="mb-6 flex flex-wrap items-end gap-3">
    <div>
      <h1 className="text-2xl font-extrabold tracking-tight text-[var(--ink)] sm:text-3xl">{title}</h1>
      {sub && <p className="mt-1 text-sm text-muted-foreground">{sub}</p>}
    </div>
    <div className="ml-auto flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Field = ({ label, hint, warn, children, className = "" }) => (
  <label className={`grid gap-1 text-xs font-semibold text-[var(--ink)]/80 ${className}`}>
    <span className="flex items-center gap-2">{label}{warn && <span className="rounded bg-orange-100 px-1.5 text-[10px] font-bold text-orange-700">{warn}</span>}</span>
    {children}
    {hint && <span className="text-[11px] font-normal text-muted-foreground">{hint}</span>}
  </label>
);

export const Sel = ({ value, onChange, options, testid, placeholder, ...rest }) => (
  <select className={inputCls} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} {...rest}>
    {placeholder !== undefined && <option value="">{placeholder}</option>}
    {options.map((o) => (Array.isArray(o) ? <option key={o[0]} value={o[0]}>{o[1]}</option> : <option key={o} value={o}>{o}</option>))}
  </select>
);

const RT = {
  real_test: ["REAL – TEST ONLY", "bg-red-600 text-white"],
  sample: ["SAMPLE", "bg-[var(--orange)] text-white"],
  demo: ["DEMO", "bg-[var(--lav-2)] text-[var(--violet)]"],
};
export const RecordBadge = ({ type }) => {
  const [l, c] = RT[type] || RT.demo;
  return <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide ${c}`} data-testid={`record-badge-${type || "demo"}`}>{l}</span>;
};

const RC = { answered: "bg-green-100 text-green-800", not_answered: "bg-red-100 text-red-700", failed: "bg-red-600 text-white", busy: "bg-orange-100 text-orange-700", pending: "bg-gray-100 text-gray-700" };
export const ResultBadge = ({ r }) => <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${RC[r] || RC.pending}`}>{({ answered: "answered", not_answered: "not answered", failed: "failed", busy: "busy", pending: "in progress" })[r] || r}</span>;

export const FlagList = ({ flags, testid = "flags" }) => (
  <div data-testid={testid}>
    {!flags?.length ? <Empty>No flagged patients.</Empty> : (
      <ul className="grid gap-2">
        {flags.map((f) => (
          <li key={f.patient_id + f.type} className={`flex flex-wrap items-center gap-2 rounded-2xl px-3 py-2 text-sm ${f.type === "pressed_9" ? "bg-purple-50" : "bg-orange-50"}`} data-testid={`flag-${f.type}-${f.patient_id}`}>
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${f.type === "pressed_9" ? "bg-purple-200 text-purple-900" : "bg-orange-200 text-orange-900"}`}>{f.label}</span>
            <b>{f.patient_name}</b><span className="text-muted-foreground">{f.phone}</span>
            {f.date && <span className="ml-auto text-xs text-muted-foreground">{new Date(f.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</span>}
          </li>
        ))}
      </ul>
    )}
  </div>
);

export const Empty = ({ children }) => <div className="rounded-2xl border border-dashed border-[var(--lav-3)] p-8 text-center text-sm text-muted-foreground">{children}</div>;

const Cat = createContext(null);
export function CatalogProvider({ children }) {
  const [patients, reloadP] = useLoad("/patients");
  const [hospitals, reloadH] = useLoad("/hospitals");
  const [doctors, reloadD] = useLoad("/doctors");
  const reload = () => { reloadP(); reloadH(); reloadD(); };
  return <Cat.Provider value={{ patients: patients || [], hospitals: hospitals || [], doctors: doctors || [], reload }}>{children}</Cat.Provider>;
}
export const useCatalog = () => useContext(Cat);

export const PatientSelect = ({ value, onChange, testid = "patient-select" }) => {
  const { patients } = useCatalog();
  return <Sel value={value} onChange={onChange} testid={testid} placeholder="Choose patient" options={patients.map((p) => [p.id, `${p.name} (${p.age})${p.record_type === "real_test" ? " · REAL – TEST ONLY" : p.record_type === "sample" ? " · sample" : ""}`])} />;
};
export const HospitalSelect = ({ value, onChange, testid = "hospital-select", placeholder = "Choose hospital" }) => {
  const { hospitals } = useCatalog();
  return <Sel value={value} onChange={onChange} testid={testid} placeholder={placeholder} options={hospitals.map((h) => [h.id, h.name])} />;
};
